import type {MockEvidence} from "../../types";

export const days = (n: number) => new Date(Date.now() - n * 86400000);

export const PROCESS_EVIDENCE: Record<string, MockEvidence[]> = {
  "proc-vendor-invoice": [
    {
      id: "ev-vi-01",
      citationId: 1,
      name: "Purchasing Policy Manual v2.3",
      sourceType: "Document",
      scope: "Accounts Payable",
      context: "Section 6.3 — Three-way match requirements",
    },
    {
      id: "ev-vi-02",
      citationId: 2,
      name: "AP-04 Three-Way Match Screenshot",
      sourceType: "Image",
      scope: "Accounts Payable",
      context: "SAP system screenshot showing match validation",
    },
    {
      id: "ev-vi-03",
      citationId: 3,
      name: "Procurement SOP v1.8",
      sourceType: "Document",
      scope: "Procurement",
      context: "Receiving department verification procedures",
    },
  ],
  "proc-order-fulfillment": [
    {
      id: "ev-of-01",
      citationId: 1,
      name: "Revenue Recognition Policy",
      sourceType: "Document",
      scope: "Revenue Accounting",
      context: "Section 2.1 — ASC 606 recognition criteria",
    },
    {
      id: "ev-of-02",
      citationId: 2,
      name: "Shipping Log Reconciliation Report",
      sourceType: "Document",
      scope: "Warehouse",
      context: "Daily shipment-to-invoice reconciliation",
    },
    {
      id: "ev-of-03",
      citationId: 3,
      name: "Billing System Walkthrough Recording",
      sourceType: "Video",
      scope: "Billing",
      context: "Automated invoice trigger upon shipment confirmation",
    },
  ],
  "proc-journal-entry": [
    {
      id: "ev-je-01",
      citationId: 1,
      name: "Journal Entry Policy",
      sourceType: "Document",
      scope: "General Ledger",
      context: "Dual approval and segregation of duties requirements",
    },
    {
      id: "ev-je-02",
      citationId: 2,
      name: "SAP Period Close Configuration",
      sourceType: "Image",
      scope: "General Ledger",
      context: "System screenshot of period posting restrictions",
    },
    {
      id: "ev-je-04",
      citationId: 3,
      name: "Account Code Validation Rules",
      sourceType: "Document",
      scope: "General Ledger",
      context: "Automated validation of GL account code combinations",
    },
    {
      id: "ev-je-05",
      citationId: 4,
      name: "Month-End JE Review Walkthrough",
      sourceType: "Video",
      scope: "Accounting",
      context: "Demonstration of Controller review and sign-off process",
    },
  ],
};

export const ELC_EVIDENCE: Record<string, MockEvidence[]> = {
  "elc-control-environment": [
    {
      id: "ev-ce-01",
      citationId: 1,
      name: "Audit Committee Charter (2026)",
      sourceType: "Document",
      scope: "Governance",
      context: "Board-approved charter defining audit committee responsibilities and independence requirements",
    },
    {
      id: "ev-ce-02",
      citationId: 2,
      name: "Q3 2025 Audit Committee Meeting Minutes",
      sourceType: "Document",
      scope: "Governance",
      context: "Minutes documenting agenda items, management reports reviewed, and attendance",
    },
    {
      id: "ev-ce-03",
      citationId: 3,
      name: "Code of Conduct v4.0",
      sourceType: "Document",
      scope: "Ethics & Compliance",
      context:
        "Current version covering conflicts of interest, anti-bribery, data protection, and financial reporting integrity",
    },
    {
      id: "ev-ce-04",
      citationId: 4,
      name: "Annual Ethics Training Completion Report",
      sourceType: "Document",
      scope: "Human Resources",
      context: "Training completion rates by department — 94% overall completion for the current cycle",
    },
  ],
  "elc-monitoring-governance": [
    {
      id: "ev-moc-01",
      citationId: 1,
      name: "Internal Audit Annual Plan (2026)",
      sourceType: "Document",
      scope: "Internal Audit",
      context:
        "Risk-based audit plan approved by the Audit Committee covering financial reporting, operations, and compliance",
    },
    {
      id: "ev-moc-02",
      citationId: 2,
      name: "Monthly KPI Dashboard — February 2026",
      sourceType: "Document",
      scope: "Finance",
      context:
        "Controller's monthly dashboard showing transaction accuracy, reconciliation rates, and exception volumes",
    },
    {
      id: "ev-moc-03",
      citationId: 3,
      name: "Deficiency Tracker Export — February 2026",
      sourceType: "Document",
      scope: "Internal Audit",
      context: "Spreadsheet export showing open deficiencies, severity, SLA status, and remediation owners",
    },
    {
      id: "ev-moc-04",
      citationId: 4,
      name: "SOC 1 Type II Report — Cloud Payroll Provider",
      sourceType: "Document",
      scope: "Third-Party Assurance",
      context: "Annual SOC report covering payroll processing controls for the period ending September 2025",
    },
    {
      id: "ev-moc-05",
      citationId: 5,
      name: "Exception Monitoring Configuration",
      sourceType: "Document",
      scope: "IT / Finance",
      context: "Documentation of the 47 automated exception rules configured in SAP S/4HANA",
    },
  ],
  "elc-risk-assessment": [
    {
      id: "ev-ra-01",
      citationId: 1,
      name: "Enterprise Risk Register — Q1 2026",
      sourceType: "Document",
      scope: "Risk Management",
      context:
        "Central risk register maintained by the risk management function, covering strategic, operational, financial, and compliance risks",
    },
    {
      id: "ev-ra-02",
      citationId: 2,
      name: "Risk Management Policy v2.1",
      sourceType: "Document",
      scope: "Governance",
      context:
        "Board-approved policy defining the entity's risk management framework, roles, responsibilities, and escalation procedures",
    },
    {
      id: "ev-ra-03",
      citationId: 3,
      name: "Board Risk Report — February 2026",
      sourceType: "Document",
      scope: "Governance",
      context:
        "Quarterly risk report presented to the audit committee, summarising key risk movements and emerging risks",
    },
    {
      id: "ev-ra-04",
      citationId: 4,
      name: "Change Impact Assessment Template",
      sourceType: "Document",
      scope: "Risk Management",
      context:
        "Template used by business units to assess the risk and financial reporting impact of significant changes",
    },
  ],
  "elc-it-environment": [
    {
      id: "ev-gitc-01",
      citationId: 1,
      name: "IT Access Management Policy v3.1",
      sourceType: "Document",
      scope: "IT Security",
      context: "Section 4 — User provisioning and deprovisioning procedures",
    },
    {
      id: "ev-gitc-02",
      citationId: 2,
      name: "Quarterly Access Review Report — Q3 2025",
      sourceType: "Document",
      scope: "IT Security",
      context: "User access recertification results and exceptions",
    },
    {
      id: "ev-gitc-03",
      citationId: 3,
      name: "Change Management Policy v2.0",
      sourceType: "Document",
      scope: "IT Operations",
      context: "CAB approval workflow and production deployment procedures",
    },
    {
      id: "ev-gitc-04",
      citationId: 4,
      name: "Privileged Access Monitoring Dashboard",
      sourceType: "Image",
      scope: "IT Security",
      context: "Screenshot of SIEM dashboard showing admin account activity logs",
    },
  ],
};
